import React, { useState } from 'react';
import { profileService } from '../services/profileService';
import { UserProfile, ServiceType, SERVICE_LABELS } from '../types';
import { AuthUser } from '../lib/supabase';
import { formatCurrency, formatBRLNumber, parseBRLNumber } from '../lib/formatters';

interface ProfileSettingsProps {
  user: AuthUser;
  profile: UserProfile | null;
  setProfile: (profile: UserProfile | null) => void;
  onLogout: () => void;
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({ user, profile, setProfile, onLogout }) => {
  const [localProfile, setLocalProfile] = useState<UserProfile | null>(profile);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handlePriceChange = (service: ServiceType, value: string) => {
    if (!localProfile) return;
    const numericValue = parseBRLNumber(value);
    setLocalProfile({
      ...localProfile,
      prices: {
        ...localProfile.prices,
        [service]: numericValue
      }
    });
  };

  const handleInfoChange = (field: 'full_name' | 'company_name', value: string) => {
    if (!localProfile) return;
    setLocalProfile({ ...localProfile, [field]: value });
  };

  const handleAddCustomService = () => {
    if (!localProfile) return;
    const newService = {
      id: `custom_${Date.now()}`,
      name: 'Novo Serviço',
      price: 0,
      category: 'area' as const
    };
    setLocalProfile({
      ...localProfile,
      custom_services: [...(localProfile.custom_services || []), newService]
    });
    setIsEditing(true);
  };

  const handleCustomServiceChange = (id: string, field: string, value: any) => {
    if (!localProfile) return;
    setLocalProfile({
      ...localProfile,
      custom_services: localProfile.custom_services?.map(s =>
        s.id === id ? { ...s, [field]: value } : s
      )
    });
  };

  const handleRemoveCustomService = (id: string) => {
    if (!localProfile) return;
    setLocalProfile({
      ...localProfile,
      custom_services: localProfile.custom_services?.filter(s => s.id !== id)
    });
  };

  const handleSave = async () => {
    if (!localProfile) return;

    setSaving(true);
    const updated = await profileService.upsertProfile(user.id, localProfile);

    if (updated) {
      setProfile(updated);
      setLocalProfile(updated);
      setIsEditing(false);
      alert('Configurações da GM salvas com sucesso!');
    } else {
      alert('Erro ao salvar. Verifique se você rodou o comando SQL no Supabase conforme as instruções do assistente.');
    }

    setSaving(false);
  };

  const handleCancel = () => {
    setLocalProfile(profile);
    setIsEditing(false);
  };

  // Se não tem perfil, mostra formulário de criação
  if (!localProfile) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fa-solid fa-user-plus text-2xl text-blue-600"></i>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Complete seu Perfil</h2>
          <p className="text-slate-500 mb-6">Configure seus dados e preços para começar a usar a calculadora.</p>
          <button
            onClick={() => {
              setLocalProfile({
                id: user.id,
                full_name: '',
                company_name: '',
                prices: {
                  gesso_simples: 45.00,
                  forro_reforcado: 60.00,
                  parede_bloco: 65.00,
                  moldura: 15.00,
                  dilatacao: 12.00,
                  reboco: 35.00,
                  revestimento: 40.00,
                  reboco_separado: 35.00,
                  parede_bloco_separado: 65.00,
                  revestimento_separado: 40.00,
                },
              });
              setIsEditing(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-xl transition-all"
          >
            Configurar Perfil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
        {/* Banner do Perfil */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-10 text-white relative">
          <div className="flex items-center gap-6 relative z-10">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-3xl font-bold border-2 border-white/30 shadow-inner">
              {localProfile.full_name ? localProfile.full_name.charAt(0).toUpperCase() : 'G'}
            </div>
            <div>
              <h2 className="text-2xl font-black tracking-tight">{localProfile.full_name || 'Seu Nome'}</h2>
              <p className="text-blue-100 flex items-center gap-2 font-bold uppercase text-[10px] tracking-widest mt-1">
                <i className="fa-solid fa-building"></i>
                {localProfile.company_name || 'Sua Empresa'}
              </p>
              <p className="text-blue-100/70 text-xs mt-1 font-medium">{user.email}</p>
            </div>
          </div>
          <button
            onClick={() => isEditing ? handleCancel() : setIsEditing(true)}
            className="absolute top-6 right-6 bg-white/15 hover:bg-white text-white hover:text-blue-600 w-10 h-10 rounded-xl transition-all flex items-center justify-center shadow-sm"
          >
            <i className={`fa-solid ${isEditing ? 'fa-xmark' : 'fa-pen-to-square'}`}></i>
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Editor de Informações Básicas */}
          {isEditing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2">
              <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Seu Nome</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none font-bold text-sm"
                  value={localProfile.full_name}
                  onChange={(e) => handleInfoChange('full_name', e.target.value)}
                />
              </div>
              <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Nome da Empresa</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none font-bold text-sm"
                  value={localProfile.company_name}
                  onChange={(e) => handleInfoChange('company_name', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Lista de Preços */}
          <div>
            <h3 className="text-sm font-black text-blue-600 uppercase tracking-tighter italic mb-4 flex items-center gap-2">
              <i className="fa-solid fa-tags"></i>
              Preços de Referência GM (R$)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(Object.keys(SERVICE_LABELS) as ServiceType[]).map((service) => (
                <div key={service} className="flex items-center justify-between p-3.5 bg-slate-50 rounded-2xl border border-slate-100 transition-hover hover:border-blue-200">
                  <span className="text-[11px] font-black text-slate-500 uppercase tracking-tighter">{SERVICE_LABELS[service]}</span>
                  {isEditing ? (
                    <div className="flex items-center gap-2 max-w-[110px]">
                      <span className="text-[10px] font-black text-blue-500">R$</span>
                      <input
                        type="text"
                        className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-right text-xs font-black text-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
                        value={localProfile.prices[service]}
                        onChange={(e) => handlePriceChange(service, e.target.value)}
                      />
                    </div>
                  ) : (
                    <span className="font-black text-slate-800 text-sm tracking-tight">{formatCurrency(localProfile.prices[service])}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Serviços Extras */}
          <div>
            <div className="flex items-center justify-between mb-4 border-t border-slate-100 pt-6">
              <h3 className="text-sm font-black text-blue-600 uppercase tracking-tighter italic flex items-center gap-2">
                <i className="fa-solid fa-plus-circle text-emerald-500"></i>
                Serviços & Materiais Personalizados
              </h3>
              {isEditing && (
                <button
                  onClick={handleAddCustomService}
                  className="text-[9px] font-black bg-emerald-50 text-emerald-600 border border-emerald-100 px-3 py-1.5 rounded-lg hover:bg-emerald-100 transition-all uppercase tracking-wider"
                >
                  <i className="fa-solid fa-plus mr-1"></i> ADICIONAR NOVO
                </button>
              )}
            </div>

            {(localProfile.custom_services && localProfile.custom_services.length > 0) ? (
              <div className="space-y-3">
                {localProfile.custom_services.map((service) => (
                  <div key={service.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    {isEditing ? (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Nome do serviço"
                            className="flex-1 bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold focus:ring-2 focus:ring-blue-500 outline-none"
                            value={service.name}
                            onChange={(e) => handleCustomServiceChange(service.id, 'name', e.target.value)}
                          />
                          <button
                            onClick={() => handleRemoveCustomService(service.id)}
                            className="w-10 h-10 flex items-center justify-center bg-rose-50 text-rose-500 rounded-xl hover:bg-rose-100 transition-colors border border-rose-100"
                          >
                            <i className="fa-solid fa-trash-can"></i>
                          </button>
                        </div>
                        <div className="flex gap-3 items-center">
                          <div className="flex-1 flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
                            <span className="text-[10px] font-black text-slate-400 uppercase">R$</span>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="Preço"
                              className="w-full bg-transparent border-none p-0 text-xs font-black focus:ring-0 outline-none"
                              value={service.price}
                              onChange={(e) => handleCustomServiceChange(service.id, 'price', parseFloat(e.target.value) || 0)}
                            />
                          </div>
                          <select
                            className="flex-1 bg-white border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-black uppercase focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
                            value={service.category}
                            onChange={(e) => handleCustomServiceChange(service.id, 'category', e.target.value)}
                          >
                            <option value="area">Por m² (Área)</option>
                            <option value="linear">Por metro (Linear)</option>
                            <option value="parede">Parede (Área Parede)</option>
                            <option value="unidade">Valor Fixo (Unidade)</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-black text-slate-800 block text-xs uppercase tracking-tight">{service.name}</span>
                          <span className="text-[8px] font-black text-blue-500/70 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded-md uppercase tracking-widest mt-1 inline-block">
                            {service.category === 'area' ? 'Por m²' : service.category === 'linear' ? 'Por metro' : service.category === 'parede' ? 'Parede' : 'Valor Fixo'}
                          </span>
                        </div>
                        <span className="font-black text-blue-600 text-lg tracking-tighter">{formatCurrency(service.price)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50">
                <i className="fa-solid fa-box-open text-slate-300 mb-1 block text-lg"></i>
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Nenhum serviço personalizado</span>
              </div>
            )}
          </div>

          {/* Botões de Ação */}
          {isEditing && (
            <div className="flex gap-3 pt-6 border-t border-slate-100">
              <button
                onClick={handleCancel}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 font-black py-4 rounded-2xl text-[10px] uppercase tracking-widest transition-all active:scale-95"
              >
                <i className="fa-solid fa-xmark mr-2"></i>
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-black py-4 rounded-2xl text-[10px] uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-blue-500/20"
              >
                {saving ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/50 border-t-white"></div>
                    <span>Salvando...</span>
                  </div>
                ) : (
                  <>
                    <i className="fa-solid fa-check mr-2"></i>
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Botão de Sair */}
      <div className="bg-white/50 backdrop-blur-sm p-6 rounded-[2rem] border border-slate-200 flex items-center justify-between shadow-sm">
        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Sair do painel GM?</p>
          <p className="font-black text-slate-800">Sessão Ativa</p>
        </div>
        <button
          onClick={onLogout}
          className="bg-red-500/10 text-red-500 hover:bg-red-500 text-sm font-black px-5 py-2.5 rounded-full hover:text-white transition-all flex items-center gap-2 border border-red-500/20"
        >
          <i className="fa-solid fa-right-from-bracket"></i>
          SAIR DO SISTEMA
        </button>
      </div>
    </div>
  );
};

export default ProfileSettings;
